import React, { useMemo, useState } from 'react';
import { Row, Col, Select, Table, InputNumber, Button } from 'antd';
import { cloneDeep } from 'lodash';
import { DownOutlined, UpOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { draw11Info, engine11Info, driver11Info, driver5placeInfo, teammatesInfo } from '../../eleven.data';
import styles from './index.module.css';

const Eleven = () => {
    const [elevenConfig, setElevenConfig] = useState({
        draw: '0影',
        driver: '2鸟+4火',
        driver5place: '5号位火伤',
        driverSubstatsAtkRate: 6,
        driverSubstatsCritical: 25,
        a: 2,
        e: 1,
        q: 1,
        z: 1,
        engine: ['1阶硫磺石', '1阶心弦'],
    });

    const [teamConfig, setTeamConfig] = useState([
        {
            type: '耀嘉音',
            draw: '0影',
            engine: '专武1阶'
        },
        {
            type: '莱特',
            draw: '0影',
            engine: '专武1阶'
        }
    ]);

    const [enemyConfig, setEnemyConfig] = useState({
        resistance: 20,
    });

    const [expandedItems, setExpandedItems] = useState([]);

    const calculateDamage = (config, teammates) => {
        if (!config || !teammates || teammates.length !== 2) {
            console.error('Invalid config or teammates:', { config, teammates });
            return 0;
        }

        const draw11Data = draw11Info[config.draw] || {};
        const driver11Data = driver11Info[config.driver] || {};
        const driver5placeData = driver5placeInfo[config.driver5place] || {};

        const teammateData = teammates.map(member => {
            if (!member || !member.type || !teammatesInfo[member.type]) {
                console.error('Invalid teammate config:', member);
                return { name: member?.type || 'unknown', drawData: {}, engineData: {} };
            }

            const drawData = teammatesInfo[member.type].draw[member.draw] || {};
            const engineData = teammatesInfo[member.type].engine[member.engine] || {};
            return {
                name: member.type,
                drawData,
                engineData,
            };
        });

        const configs = [
            draw11Data,
            driver11Data,
            driver5placeData,
            teammateData[0].drawData,
            teammateData[0].engineData,
            teammateData[1].drawData,
            teammateData[1].engineData,
            {
                outerAtkRate: 0.03 * (config.driverSubstatsAtkRate || 0),
                critical: 0.048 * (config.driverSubstatsCritical || 0),
            },
        ];

        if (!engine11Info[config.engine]) {
            console.error('Invalid engine config:', config.engine);
            return 0;
        }

        const basicData = cloneDeep(engine11Info[config.engine]);
        basicData.basicAtk = basicData.basicAtk || 0;
        basicData.outerAtkRate = basicData.outerAtkRate || 0;
        basicData.outerAtk = basicData.outerAtk || 0;
        basicData.innerAtkRate = basicData.innerAtkRate || 0;
        basicData.innerAtk = basicData.innerAtk || 0;
        basicData.critical = basicData.critical || 0;
        basicData.resistance = basicData.resistance || { a: 0, e: 0, q: 0, z: 0 };
        basicData.damageRate = basicData.damageRate || { a: 1, e: 1, q: 1, z: 1 };
        basicData.unbalance = basicData.unbalance || 0;

        for (const conf of configs) {
            if (!conf) continue;
            Object.keys(conf).forEach(key => {
                if (typeof conf[key] === 'number') {
                    basicData[key] = (basicData[key] || 0) + conf[key];
                } else if (typeof conf[key] === 'object' && conf[key] !== null) {
                    if (!basicData[key]) {
                        basicData[key] = {};
                    }
                    Object.keys(conf[key]).forEach(subKey => {
                        basicData[key][subKey] = (basicData[key][subKey] || 0) + (conf[key][subKey] || 0);
                    });
                }
            });
        }

        const finalAtk = (basicData.basicAtk * (1 + basicData.outerAtkRate) + basicData.outerAtk) * (1 + basicData.innerAtkRate) + basicData.innerAtk;
        const finalCritical = basicData.critical >= 4 ? basicData.critical - 1 : 1 + basicData.critical * basicData.critical / 8;
        const finalUnbalance = 1 + (basicData.unbalance || 0);
        const finalAResistance = Math.min(-enemyConfig.resistance / 100 - (basicData.resistance.a || 0), 1);
        const finalEResistance = Math.min(-enemyConfig.resistance / 100 - (basicData.resistance.e || 0), 1);
        const finalQResistance = Math.min(-enemyConfig.resistance / 100 - (basicData.resistance.q || 0), 1);
        const finalZResistance = Math.min(-enemyConfig.resistance / 100 - (basicData.resistance.z || 0), 1);
        // 计算防御乘区
        const finalDefence = 1 / (1.2 * (1 - (basicData.uvt || 0)) * (1 + (basicData.defense || 0)) + 1);

        const damage = finalAtk * finalCritical * finalUnbalance * finalDefence * (
            (1 + finalAResistance) * (basicData.damageRate.a || 1) * 13.845 * 2 * (config.a || 0)
            + (1 + finalEResistance) * (basicData.damageRate.e || 1) * 15.96 * (config.e || 0)
            + (1 + finalQResistance) * (basicData.damageRate.q || 1) * 49.71 * (config.q || 0)
            + (1 + finalZResistance) * (basicData.damageRate.z || 1) * 14.95 * (config.z || 0)
        );

        return damage;
    };

    const calculateFinalAttributes = (weapon, teammates, ratio) => {
        const config = {
            ...elevenConfig,
            engine: weapon
        };

        const draw11Data = draw11Info[config.draw] || {};
        const driver11Data = driver11Info[config.driver] || {};
        const driver5placeData = driver5placeInfo[config.driver5place] || {};

        const teammateData = teammates.map(member => {
            const drawData = teammatesInfo[member.type].draw[member.draw] || {};
            const engineData = teammatesInfo[member.type].engine[member.engine] || {};
            return {
                name: member.type,
                drawData,
                engineData,
            };
        });

        const configs = [
            draw11Data,
            driver11Data,
            driver5placeData,
            teammateData[0].drawData,
            teammateData[0].engineData,
            teammateData[1].drawData,
            teammateData[1].engineData,
            {
                outerAtkRate: 0.03 * (config.driverSubstatsAtkRate || 0),
                critical: 0.048 * (config.driverSubstatsCritical || 0),
            },
        ];

        const basicData = cloneDeep(engine11Info[weapon]);
        basicData.basicAtk = basicData.basicAtk || 0;
        basicData.outerAtkRate = basicData.outerAtkRate || 0;
        basicData.outerAtk = basicData.outerAtk || 0;
        basicData.innerAtkRate = basicData.innerAtkRate || 0;
        basicData.innerAtk = basicData.innerAtk || 0;
        basicData.critical = basicData.critical || 0;
        basicData.resistance = basicData.resistance || { a: 0, e: 0, q: 0, z: 0 };
        basicData.damageRate = basicData.damageRate || { a: 1, e: 1, q: 1, z: 1 };
        basicData.unbalance = basicData.unbalance || 0;

        for (const conf of configs) {
            if (!conf) continue;
            Object.keys(conf).forEach(key => {
                if (typeof conf[key] === 'number') {
                    basicData[key] = (basicData[key] || 0) + conf[key];
                } else if (typeof conf[key] === 'object' && conf[key] !== null) {
                    if (!basicData[key]) {
                        basicData[key] = {};
                    }
                    Object.keys(conf[key]).forEach(subKey => {
                        basicData[key][subKey] = (basicData[key][subKey] || 0) + (conf[key][subKey] || 0);
                    });
                }
            });
        }

        return basicData;
    };

    const renderAttributes = (weapon, teammates, ratio) => {
        const itemData = calculateFinalAttributes(weapon, teammates, ratio);
        const finalAtk = (itemData.basicAtk * (1 + itemData.outerAtkRate) + itemData.outerAtk) * (1 + itemData.innerAtkRate) + itemData.innerAtk;
        // 计算防御乘区
        const finalDefence = 1 / (1.2 * (1 - (itemData.uvt || 0)) * (1 + (itemData.defense || 0)) + 1);

        return (
            <div className={styles.attributeContent}>
                <div className={styles.line}>
                    基础攻击：{itemData.basicAtk.toFixed(0)}
                </div>
                <div className={styles.line}>
                    局外攻击：{(itemData.basicAtk * (1 + itemData.outerAtkRate) + itemData.outerAtk).toFixed(0)}
                </div>
                <div className={styles.line}>
                    局内攻击：{finalAtk.toFixed(0)}
                </div>
                <div className={styles.line}>
                    暴击率：{itemData.critical >= 4 ? '100%' : `${(itemData.critical * 25).toFixed(1)}%`}
                </div>
                <div className={styles.line}>
                    暴击伤害：{itemData.critical >= 4 ? `${(itemData.critical * 100 - 200).toFixed(1)}%` : `${(itemData.critical * 50).toFixed(1)}%`}
                </div>
                <div className={styles.line}>
                    易伤失衡：{((itemData.unbalance || 0) * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    穿透率：{((itemData.uvt || 0) * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    减防：{((itemData.defense || 0) * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    防御乘区：{(finalDefence).toFixed(3)}
                </div>
                <div className={styles.line}>
                    强化特殊技抗性：{(enemyConfig.resistance + itemData.resistance.e * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    终结技抗性：{(enemyConfig.resistance + itemData.resistance.q * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    连携技抗性：{(enemyConfig.resistance + itemData.resistance.z * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    普攻抗性：{(enemyConfig.resistance + itemData.resistance.a * 100).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    强化特殊技伤害加成：{((itemData.damageRate.e * 100 - 100) || 0).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    终结技伤害加成：{((itemData.damageRate.q * 100 - 100) || 0).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    连携技伤害加成：{((itemData.damageRate.z * 100 - 100) || 0).toFixed(1)}%
                </div>
                <div className={styles.line}>
                    普攻伤害加成：{((itemData.damageRate.a * 100 - 100) || 0).toFixed(1)}%
                </div>
            </div>
        );
    };

    const addTeammate = () => {
        const usedTypes = teamConfig.map(t => t.type);
        const availableTypes = Object.keys(teammatesInfo).filter(t => !usedTypes.includes(t));

        if (availableTypes.length > 0) {
            const newType = availableTypes[0];
            setTeamConfig([...teamConfig, {
                type: newType,
                draw: Object.keys(teammatesInfo[newType].draw)[0],
                engine: Object.keys(teammatesInfo[newType].engine)[0]
            }]);
        }
    };

    const removeTeammate = (index) => {
        setTeamConfig(prev => prev.filter((_, i) => i !== index));
    };

    // 生成所有可能的队友组合
    const generateTeamCombinations = () => {
        const combinations = [];
        for (let i = 0; i < teamConfig.length - 1; i++) {
            for (let j = i + 1; j < teamConfig.length; j++) {
                combinations.push([teamConfig[i], teamConfig[j]]);
            }
        }
        return combinations;
    };

    const data = useMemo(() => {
        try {
            const allResults = [];
            const teamCombinations = generateTeamCombinations();

            // 计算所有武器和队友组合的伤害
            elevenConfig.engine.forEach(weapon => {
                teamCombinations.forEach(([teammate1, teammate2]) => {
                    const damage = calculateDamage({
                        ...elevenConfig,
                        engine: weapon
                    }, [teammate1, teammate2]);

                    allResults.push({
                        key: `${weapon}-${teammate1.type}-${teammate2.type}`,
                        weapon,
                        teammate1: `${teammate1.type}(${teammate1.draw}-${teammate1.engine})`,
                        teammate2: `${teammate2.type}(${teammate2.draw}-${teammate2.engine})`,
                        damage
                    });
                });
            });

            // 按伤害降序排序
            allResults.sort((a, b) => b.damage - a.damage);

            // 计算伤害比例
            const minDamage = Math.min(...allResults.map(item => item.damage));
            return allResults.map(item => ({
                ...item,
                ratio: item.damage / minDamage
            }));
        } catch (error) {
            console.error('Error calculating data:', error);
            return [];
        }
    }, [elevenConfig, teamConfig, enemyConfig]);

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.background} />
            <Row gutter={24} className={styles.contentRow}>
                <Col xs={24} md={8}>
                    <div className={styles.card}>
                        <div className={styles.title}>11号配置</div>
                        <div className={styles.subtitle}>影画</div>
                        <Select
                            value={elevenConfig.draw}
                            onChange={draw => setElevenConfig(c => ({ ...c, draw }))}
                            className={styles.select}
                            style={{ width: '100%' }}
                        >
                            {Object.keys(draw11Info).map(key => (
                                <Select.Option key={key} value={key}>{key}</Select.Option>
                            ))}
                        </Select>

                        <div className={styles.subtitle}>驱动盘套装</div>
                        <Select
                            value={elevenConfig.driver}
                            onChange={driver => setElevenConfig(c => ({ ...c, driver }))}
                            className={styles.select}
                            style={{ width: '100%' }}
                        >
                            {Object.keys(driver11Info).map(key => (
                                <Select.Option key={key} value={key}>{key}</Select.Option>
                            ))}
                        </Select>

                        <div className={styles.subtitle}>驱动盘5号位</div>
                        <Select
                            value={elevenConfig.driver5place}
                            onChange={driver5place => setElevenConfig(c => ({ ...c, driver5place }))}
                            className={styles.select}
                            style={{ width: '100%' }}
                        >
                            {Object.keys(driver5placeInfo).map(key => (
                                <Select.Option key={key} value={key}>{key}</Select.Option>
                            ))}
                        </Select>

                        <div className={styles.subtitle}>驱动盘攻击副词条</div>
                        <InputNumber
                            min={0}
                            value={elevenConfig.driverSubstatsAtkRate}
                            onChange={driverSubstatsAtkRate => setElevenConfig(c => ({ ...c, driverSubstatsAtkRate }))}
                            suffix={'* 3%'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>驱动盘双暴副词条</div>
                        <InputNumber
                            min={0}
                            value={elevenConfig.driverSubstatsCritical}
                            onChange={driverSubstatsCritical => setElevenConfig(c => ({ ...c, driverSubstatsCritical }))}
                            suffix={'* 0.048%'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>强化特殊技</div>
                        <InputNumber
                            min={0}
                            value={elevenConfig.e}
                            onChange={e => setElevenConfig(c => ({ ...c, e }))}
                            suffix={'次'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>终结技</div>
                        <InputNumber
                            min={0}
                            value={elevenConfig.q}
                            onChange={q => setElevenConfig(c => ({ ...c, q }))}
                            suffix={'次'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>连携技</div>
                        <InputNumber
                            min={0}
                            value={elevenConfig.z}
                            onChange={z => setElevenConfig(c => ({ ...c, z }))}
                            suffix={'次'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>火刀8段</div>
                        <InputNumber
                            min={0}
                            max={elevenConfig.e + elevenConfig.q + elevenConfig.z}
                            value={elevenConfig.a}
                            onChange={a => setElevenConfig(c => ({ ...c, a }))}
                            suffix={'组'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />

                        <div className={styles.subtitle}>对比音擎</div>
                        <Select
                            mode={'multiple'}
                            value={elevenConfig.engine}
                            onChange={engine => setElevenConfig(c => ({ ...c, engine: Array.isArray(engine) ? engine : [engine] }))}
                            className={styles.select}
                            style={{ width: '100%' }}
                        >
                            {Object.keys(engine11Info).map(key => (
                                <Select.Option key={key} value={key}>{key}</Select.Option>
                            ))}
                        </Select>
                    </div>
                </Col>

                <Col xs={24} md={8}>
                    <div className={styles.card}>
                        <div className={styles.title}>队友配置</div>
                        {teamConfig.map((member, index) => (
                            <div key={member.type} style={{ marginBottom: 26 }}>
                                <div className={styles.teammateHeader}>
                                    <div className={styles.subtitle}>队友{index + 1}</div>
                                    {teamConfig.length > 2 && (
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeTeammate(index)}
                                        />
                                    )}
                                </div>

                                <div className={styles.subtitle}>角色</div>
                                <Select
                                    value={member.type}
                                    onChange={type => {
                                        const newTeam = [...teamConfig];
                                        newTeam[index] = {
                                            ...newTeam[index],
                                            type,
                                            draw: Object.keys(teammatesInfo[type].draw)[0],
                                            engine: Object.keys(teammatesInfo[type].engine)[0]
                                        };
                                        setTeamConfig(newTeam);
                                    }}
                                    className={styles.select}
                                    style={{ width: '100%' }}
                                >
                                    {Object.keys(teammatesInfo)
                                        .filter(t => !teamConfig.filter((_, i) => i !== index).map(m => m.type).includes(t))
                                        .map(type => (
                                            <Select.Option key={type} value={type}>{type}</Select.Option>
                                        ))}
                                </Select>

                                <div className={styles.subtitle}>{member.type}影画</div>
                                <Select
                                    value={member.draw}
                                    onChange={draw => {
                                        const newTeam = [...teamConfig];
                                        newTeam[index] = {
                                            ...newTeam[index],
                                            draw
                                        };
                                        setTeamConfig(newTeam);
                                    }}
                                    className={styles.select}
                                    style={{ width: '100%' }}
                                >
                                    {Object.keys(teammatesInfo[member.type].draw)
                                        .map(key => (
                                            <Select.Option key={key} value={key}>{key}</Select.Option>
                                        ))}
                                </Select>

                                <div className={styles.subtitle}>{member.type}音擎</div>
                                <Select
                                    value={member.engine}
                                    onChange={engine => {
                                        const newTeam = [...teamConfig];
                                        newTeam[index] = {
                                            ...newTeam[index],
                                            engine
                                        };
                                        setTeamConfig(newTeam);
                                    }}
                                    className={styles.select}
                                    style={{ width: '100%' }}
                                >
                                    {Object.keys(teammatesInfo[member.type].engine)
                                        .map(key => (
                                            <Select.Option key={key} value={key}>{key}</Select.Option>
                                        ))}
                                </Select>
                            </div>
                        ))}

                        <Button
                            type="dashed"
                            block
                            icon={<PlusOutlined />}
                            onClick={addTeammate}
                            disabled={teamConfig.length >= Object.keys(teammatesInfo).length}
                        >
                            添加队友
                        </Button>

                        <div className={styles.title} style={{ marginTop: 20 }}>敌人配置</div>
                        <div className={styles.subtitle}>抗性</div>
                        <InputNumber
                            min={-20}
                            max={100}
                            value={enemyConfig.resistance}
                            onChange={resistance => setEnemyConfig({ resistance: resistance })}
                            suffix={'%'}
                            className={styles.select}
                            style={{ width: '100%' }}
                        />
                    </div>
                </Col>

                <Col xs={24} md={8}>
                    <div className={styles.card}>
                        <div className={styles.title}>伤害排名</div>
                        <Table
                            columns={[
                                {
                                    title: '武器',
                                    dataIndex: 'weapon',
                                    width: '25%',
                                },
                                {
                                    title: '队友1',
                                    dataIndex: 'teammate1',
                                    width: '30%',
                                    render: (text) => {
                                        const match = text.match(/(.+?)\((.+?)-(.+?)\)/);
                                        if (!match) return text;
                                        const [_, type, draw, engine] = match;
                                        return `${type}(${draw}-${engine})`;
                                    }
                                },
                                {
                                    title: '队友2',
                                    dataIndex: 'teammate2',
                                    width: '30%',
                                    render: (text) => {
                                        const match = text.match(/(.+?)\((.+?)-(.+?)\)/);
                                        if (!match) return text;
                                        const [_, type, draw, engine] = match;
                                        return `${type}(${draw}-${engine})`;
                                    }
                                },
                                {
                                    title: '伤害比例',
                                    dataIndex: 'ratio',
                                    width: '15%',
                                    render: (value) => value.toFixed(2)
                                }
                            ]}
                            dataSource={data}
                            pagination={{
                                pageSize: 50,
                                defaultCurrent: 1,
                            }}
                            size="small"
                            scroll={{ y: 400 }}
                        />
                        <div className={styles.title}>最终局内属性</div>
                        {data.map(item => {
                            const teammate1Match = item.teammate1.match(/(.+?)\((.+?)-(.+?)\)/);
                            const teammate2Match = item.teammate2.match(/(.+?)\((.+?)-(.+?)\)/);
                            if (!teammate1Match || !teammate2Match) return null;

                            const teammates = [
                                {
                                    type: teammate1Match[1],
                                    draw: teammate1Match[2],
                                    engine: teammate1Match[3]
                                },
                                {
                                    type: teammate2Match[1],
                                    draw: teammate2Match[2],
                                    engine: teammate2Match[3]
                                }
                            ];

                            return (
                                <div key={item.key} className={styles.attributeSection}>
                                    <div
                                        className={styles.attributeHeader}
                                        onClick={() => {
                                            setExpandedItems(prev =>
                                                prev.includes(item.key)
                                                    ? prev.filter(i => i !== item.key)
                                                    : [...prev, item.key]
                                            );
                                        }}
                                    >
                                        <span>
                                            {item.weapon} - {teammates[0].type}+{teammates[1].type}
                                            (伤害比例: {item.ratio.toFixed(2)})
                                        </span>
                                        {expandedItems.includes(item.key) ? <UpOutlined /> : <DownOutlined />}
                                    </div>
                                    {expandedItems.includes(item.key) && renderAttributes(item.weapon, teammates, item.ratio)}
                                </div>
                            );
                        })}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Eleven;